import React, { useState, useRef, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient, useMutation } from 'react-query';
import api from '../services/api';
import { routes } from '../lib/routes';
import './AppLayout.css';

function canSeeTecnico(tipo) {
  return ['admin', 'gestor', 'analista', 'responsavel_tecnico'].includes(tipo);
}

function canSeeAdminSisobra(tipo) {
  return ['admin', 'gestor'].includes(tipo);
}

function canSeeAdminUsuarios(tipo) {
  return tipo === 'admin';
}

export default function AppLayout({ title, subtitle, children, actions }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const notifRef = useRef(null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: user } = useQuery('me', () => api.get('/usuarios/me').then((r) => r.data), {
    retry: false,
  });

  const { data: notificacoes = [] } = useQuery(
    'notificacoes-unread',
    () => api.get('/notificacoes').then((r) => r.data),
    { refetchInterval: 60_000 }
  );

  const markLida = useMutation((id) => api.put(`/notificacoes/${id}/lida`), {
    onSuccess: () => queryClient.invalidateQueries('notificacoes-unread'),
  });

  useEffect(() => {
    const close = (e) => {
      if (notifRef.current && !notifRef.current.contains(e.target)) setNotifOpen(false);
    };
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('token');
    queryClient.removeQueries('me');
    queryClient.removeQueries('notificacoes-unread');
    navigate(routes.login, { replace: true });
  };

  const tipo = user?.tipo;
  const showTecnico = canSeeTecnico(tipo);
  const showSisobra = canSeeAdminSisobra(tipo);
  const showUsuarios = canSeeAdminUsuarios(tipo);

  return (
    <div className="app-shell">
      <aside className={`app-sidebar ${menuOpen ? 'open' : ''}`}>
        <div className="app-brand">
          <a className="app-brand-link" href="/SisPro" title="Pagina institucional SisPro">
            <span className="app-brand-badge">SP</span>
            <div>
              <h2>SisPro</h2>
              <p>Protocolos eletronicos</p>
            </div>
          </a>
        </div>

        <nav className="app-nav" aria-label="Menu principal">
          <p className="app-nav-section">Operacao</p>
          <NavLink to={routes.dashboard} end onClick={() => setMenuOpen(false)}>
            <span className="app-nav-icon" aria-hidden="true">◫</span>
            <span>Dashboard</span>
          </NavLink>
          <NavLink to={routes.protocolos} onClick={() => setMenuOpen(false)}>
            <span className="app-nav-icon" aria-hidden="true">◧</span>
            <span>Protocolos</span>
          </NavLink>
          <NavLink to={routes.novoProtocolo} onClick={() => setMenuOpen(false)}>
            <span className="app-nav-icon" aria-hidden="true">＋</span>
            <span>Novo protocolo</span>
          </NavLink>

          {showTecnico ? (
            <>
              <p className="app-nav-section">Tecnico</p>
              <NavLink to={routes.tecnicoAnalises} onClick={() => setMenuOpen(false)}>
                <span className="app-nav-icon" aria-hidden="true">⌁</span>
                <span>Analises</span>
              </NavLink>
              <NavLink to={routes.tecnicoVistorias} onClick={() => setMenuOpen(false)}>
                <span className="app-nav-icon" aria-hidden="true">◎</span>
                <span>Vistorias</span>
              </NavLink>
            </>
          ) : null}

          {showSisobra || showUsuarios ? (
            <>
              <p className="app-nav-section">Administracao</p>
              {showSisobra ? (
                <NavLink to={routes.adminSisobra} onClick={() => setMenuOpen(false)}>
                  <span className="app-nav-icon" aria-hidden="true">⇄</span>
                  <span>Integracao Sisobra</span>
                </NavLink>
              ) : null}
              {showUsuarios ? (
                <NavLink to={routes.adminUsuarios} onClick={() => setMenuOpen(false)}>
                  <span className="app-nav-icon" aria-hidden="true">◉</span>
                  <span>Usuarios</span>
                </NavLink>
              ) : null}
            </>
          ) : null}
        </nav>
      </aside>
      {menuOpen ? (
        <button type="button" className="app-sidebar-overlay" onClick={() => setMenuOpen(false)} aria-label="Fechar menu" />
      ) : null}

      <div className="app-main">
        <header className="app-header">
          <button type="button" className="app-menu-btn sispro-btn sispro-btn--secondary sispro-btn--sm" onClick={() => setMenuOpen((v) => !v)}>
            Menu
          </button>
          <div className="app-header-titles">
            <h1>{title}</h1>
            {subtitle ? <p>{subtitle}</p> : null}
          </div>
          <div className="app-header-actions">
            <div className="app-notif-wrap" ref={notifRef}>
              <button
                type="button"
                className="sispro-btn sispro-btn--secondary sispro-btn--sm app-notif-trigger"
                onClick={(e) => {
                  e.stopPropagation();
                  setNotifOpen((o) => !o);
                }}
                aria-expanded={notifOpen}
                aria-label="Notificacoes"
              >
                Alertas
                {notificacoes.length ? <span className="app-notif-badge">{notificacoes.length}</span> : null}
              </button>
              {notifOpen ? (
                <div className="app-notif-dropdown" role="menu">
                  <div className="app-notif-dropdown-head">
                    <span>Notificacoes</span>
                    <button type="button" className="sispro-btn sispro-btn--ghost sispro-btn--sm" onClick={() => navigate(routes.protocolos)}>
                      Ver protocolos
                    </button>
                  </div>
                  <ul className="app-notif-list">
                    {notificacoes.length === 0 ? (
                      <li className="app-notif-empty">Nenhuma notificacao nova.</li>
                    ) : (
                      notificacoes.slice(0, 12).map((n) => (
                        <li key={n.id}>
                          <button
                            type="button"
                            className="app-notif-item"
                            onClick={() => {
                              markLida.mutate(n.id);
                              if (n.relacionado_a) navigate(routes.protocolo(n.relacionado_a));
                              setNotifOpen(false);
                            }}
                          >
                            <strong>{n.titulo}</strong>
                            <span>{n.mensagem}</span>
                          </button>
                        </li>
                      ))
                    )}
                  </ul>
                </div>
              ) : null}
            </div>
            <span className="app-user-name">{user?.nome || 'Usuario'}</span>
            <button type="button" className="sispro-btn sispro-btn--primary sispro-btn--sm" onClick={handleLogout}>
              Sair
            </button>
          </div>
        </header>

        <main className="app-content">
          {actions ? <div className="app-content-actions">{actions}</div> : null}
          {children}
        </main>
      </div>
    </div>
  );
}
